/**
 * The requisition state machine, on the client, and the words for its six statuses.
 *
 * The recruiting analogue of `web/src/incidents/state-machine.ts`, and it is treated
 * the same way: the transition set the screen offers is **derived from this table**,
 * so an illegal move has no button rather than a disabled one, and the server's
 * `409 WORKFORCE_INVALID_STATE_TRANSITION` — carrying `details.allowed_transitions[]`
 * — is what actually decides. This copy exists so the screen can offer the legal
 * moves *before* a refusal; the refusal's list is authoritative and is printed
 * verbatim when the two ever disagree.
 *
 * ── The machine (transcribed from `src/modules/recruiting/transitions.ts`) ───
 *
 *   draft    → open
 *   open     → on_hold | filled | cancelled | closed
 *   on_hold  → open
 *   filled / cancelled / closed — terminal, no move out
 *
 * The server owns the machine; a `WORKFORCE_INVALID_STATE_TRANSITION` is the wall
 * that still stands when this file is wrong. It is transcribed rather than imported,
 * because the SPA bundle is served to browsers and a `web/ → src/modules/` import is
 * a precedent whose second use drags a `pg` handle into the browser. `draft` and
 * `on_hold` each offer exactly one move; the terminal three offer none.
 */

import type { MessageKey } from '../i18n/messages';
import type { Severity } from '../states/icons';
import type { RequisitionStatus } from '../api/recruiting';

export { REQUISITION_STATUSES } from '../api/recruiting';
export type { RequisitionStatus } from '../api/recruiting';

export function isRequisitionStatus(value: unknown): value is RequisitionStatus {
  return (
    typeof value === 'string' &&
    (['draft', 'open', 'on_hold', 'filled', 'cancelled', 'closed'] as readonly string[]).includes(
      value,
    )
  );
}

/**
 * Every status has a row, the terminal three included, whose row is the empty list.
 * An absent key and an empty list are different statements; keeping the empty rows
 * makes "is this terminal" a fact read off the table rather than an omission.
 */
export const REQUISITION_TRANSITIONS: Readonly<
  Record<RequisitionStatus, readonly RequisitionStatus[]>
> = {
  draft: ['open'],
  open: ['on_hold', 'filled', 'cancelled', 'closed'],
  on_hold: ['open'],
  filled: [],
  cancelled: [],
  closed: [],
};

export function allowedTransitions(from: RequisitionStatus): readonly RequisitionStatus[] {
  return REQUISITION_TRANSITIONS[from];
}

/** `true` for exactly the statuses with no outgoing edge. Derived, never listed. */
export function isTerminalStatus(status: RequisitionStatus): boolean {
  return REQUISITION_TRANSITIONS[status].length === 0;
}

/**
 * The moves this status does **not** allow — the complement, computed rather than
 * written down, so a status cannot be missing from both lists. For the tests; no
 * component renders it (rendering it is the disabled-button shape the design rejects).
 */
export function forbiddenTransitions(from: RequisitionStatus): readonly RequisitionStatus[] {
  const allowed = new Set<RequisitionStatus>(REQUISITION_TRANSITIONS[from]);
  return (Object.keys(REQUISITION_TRANSITIONS) as RequisitionStatus[]).filter(
    (status) => !allowed.has(status),
  );
}

/** The name of a status, for the badge that says where the requisition **is**. */
export const STATUS_LABEL: Readonly<Record<RequisitionStatus, MessageKey>> = {
  draft: 'recruiting.jobs.status.draft',
  open: 'recruiting.jobs.status.open',
  on_hold: 'recruiting.jobs.status.onHold',
  filled: 'recruiting.jobs.status.filled',
  cancelled: 'recruiting.jobs.status.cancelled',
  closed: 'recruiting.jobs.status.closed',
};

/**
 * What pressing the control **does**, as a verb — not the name of the state.
 * "Đang tuyển" is a label for a status and a bad label for a button; the button says
 * what will happen. The status badge uses `STATUS_LABEL`, the transition control this.
 */
export const TRANSITION_LABEL: Readonly<Record<RequisitionStatus, MessageKey>> = {
  draft: 'recruiting.jobs.move.draft',
  open: 'recruiting.jobs.move.open',
  on_hold: 'recruiting.jobs.move.onHold',
  filled: 'recruiting.jobs.move.filled',
  cancelled: 'recruiting.jobs.move.cancelled',
  closed: 'recruiting.jobs.move.closed',
};

/**
 * The badge severity a status is drawn at — how much attention the position in the
 * workflow wants. Icon and text carry it, as everywhere; colour is the third channel.
 */
export const STATUS_BADGE: Readonly<Record<RequisitionStatus, Severity>> = {
  draft: 'info',
  open: 'high',
  on_hold: 'medium',
  filled: 'low',
  cancelled: 'low',
  closed: 'info',
};
